import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "fugu-components";
import { FilterValue, PaginableComponent, Filterer, SessionPagination, SubscriptionService } from "fugu-common";
import { FilteredTableListComponent } from "generic-pages";
import { Light, CaraUserService, LightService } from "center-services";
import { DatatableComponent } from "@siemens/ngx-datatable";

@Component({
  selector: "app-supplier-brands",
  templateUrl: "./supplier-brands.component.html",
  styleUrls: ["./supplier-brands.component.scss"],
  providers: [SubscriptionService],
})
export class SupplierBrandsComponent
  extends FilteredTableListComponent
  implements OnInit, OnChanges, AfterViewChecked, PaginableComponent {
  public static LIST_ID: string = "app-supplier-brands.brands-table";

  @Output() brandIdsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Input() canUpdate: boolean = true;

  @Input() brandIds: number[];
  @ViewChild("table") table: DatatableComponent;

  public tableControl: UntypedFormGroup;
  public rows: any[] = [];
  public allRows: any[] = [];
  public sorts: any[] = [
    {
      prop: "activated",
      dir: "desc",
    },
    {
      prop: "name",
      dir: "asc",
    },
  ];

  public brandList: Light[] = [];
  public pageNumber: number = 0;
  public filterer: Filterer;
  private sessionPagination: SessionPagination;

  constructor(
    translateService: TranslateService,
    messageService: MessageService,
    userService: CaraUserService,
    private lightService: LightService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private subscriptionService: SubscriptionService
  ) {
    super(userService, translateService, messageService);
    this.sessionPagination = new SessionPagination(this);
  }

  getPageNumber(_listId: string): number {
    return this.pageNumber;
  }

  getFilters(_listId: string): FilterValue[] {
    return this.filterer.filterValues;
  }

  getSorts(_listId: string): any[] {
    return this.sorts;
  }

  setPageNumber(_listId: string, pageNumber: number): void {
    this.pageNumber = pageNumber;
  }

  setFilters(_listId: string, filters: FilterValue[]): void {
    this.filterer.filterValues = [...filters];
    this.applyFilters();
  }

  setSorts(_listId: string, sorts: any[]): void {
    this.sorts = [...sorts];
  }

  savePaginationToSession(): void {
    this.sessionPagination.saveToSession(SupplierBrandsComponent.LIST_ID);
  }

  ngOnInit(): void {
    if (!this.tableControl) {
      this.tableControl = new UntypedFormGroup({});
    }
    this.initFilters();
    this.fetchBrandList();
    this.sessionPagination.loadFromSession(SupplierBrandsComponent.LIST_ID);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.brandIds && !changes.brandIds.firstChange) {
      this.fetchBrandList();
      this.applyFilters();
    }
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  addRow(brand: Light): void {
    const activated = this.brandIds.includes(brand.id);

    this.allRows.push({
      activated,
      id: brand.id,
      name: brand.name,
    });

    if (this.tableControl.controls[`activated_${brand.id}`]) {
      this.tableControl.controls[`activated_${brand.id}`].patchValue(activated);
    } else {
      this.tableControl.addControl(
        `activated_${brand.id}`,
        new UntypedFormControl({ value: activated, disabled: !this.canUpdate })
      );
    }
  }

  fetchBrandList(): void {
    this.allRows = [];
    this.rows = [];

    this.subscriptionService.subs.push(
      this.lightService.getBrands().subscribe(
        (brandList: Light[]) => {
          brandList.forEach((brand: Light) => {
            if (brand.archived && this.brandIds.includes(brand.id)) {
              this.brandIds.splice(this.brandIds.indexOf(brand.id), 1);
              this.brandIds = [...this.brandIds];
              this.brandIdsChange.emit(this.brandIds);
            }
          });
          this.brandList = brandList.filter(brand => !brand.archived);
          this.rows = [];

          this.brandList.forEach((brand: Light) => this.addRow(brand));
          this.rows = [...this.allRows];
          if (this.filterer.filterValues.length > 0) {
            this.applyFilters();
          }
        },
        error => {
          const title = this.translateService.instant("message.title.api-errors");
          const content = this.translateService.instant("supplier-brands-list.error.get-brands", {
            message: error.message,
          });
          this.messageService.warn(content, { title });
        }
      )
    );
  }

  checkboxOnChanges(index: number): void {
    if (!this.canUpdate) {
      return;
    }

    const activated = this.tableControl.controls[`activated_${index}`].value;
    const rowIndex = this.brandIds.findIndex(brandId => brandId === index);

    if (rowIndex !== -1 && !activated) {
      this.brandIds.splice(rowIndex, 1);
    }
    if (rowIndex === -1 && activated) {
      this.brandIds.push(index);
    }

    this.brandIds = [...this.brandIds];
    this.brandIdsChange.emit(this.brandIds);
  }

  viewBrand(event: any): void {
    if (event.type === "click") {
      if (!this.userService.canDo("BRAND_UPDATE")) {
        return;
      }

      const filteredList = this.brandList.filter(brand => brand.id === event.row.id);
      if (filteredList.length <= 0) {
        console.error(`can't fin brand with id ${event.row.id}`);
        return;
      }
      this.router.navigateByUrl(`/brand/update/${event.row.id}`);
    }
  }

  changeSortSettings(prop: string, dir: string): void {
    if (prop !== "activated") {
      this.sorts = [
        { prop: "activated", dir: "desc" },
        { prop, dir },
      ];
    } else {
      this.sorts = [{ prop, dir }];
    }

    this.rows = [...this.rows];
    this.table.sorts = this.sorts;
  }

  initFilters(): void {
    if (this.filterer) {
      return;
    }
    const componentFilterPref = this.userPreferences.filterComponents.find(
      filterPrefComponent => filterPrefComponent.component === SupplierBrandsComponent.LIST_ID
    );
    this.filterer = new Filterer(componentFilterPref?.filters);

    this.filterer.addFilter(
      "name",
      this.translateService.instant("supplier-brands-list.datatable.columns.name"),
      "string"
    );

    this.filterer.addBooleanFilter(
      "activated",
      this.translateService.instant("supplier-brands-list.datatable.columns.activated"),
      false,
      false,
      true
    );
  }

  applyFilters(): void {
    this.rows = this.filterer.filterList(this.allRows);

    this.subscriptionService.subs.push(
      this.updatePreferences(
        this.filterer.filterValues.map(fv => fv.filterId),
        SupplierBrandsComponent.LIST_ID
      ).subscribe()
    );
  }
}
